import apiService from '@/services/api.service';
import { exportScenarioToPDF, exportScenarioToCSV, exportRMFToPDF } from '@/utils/generateScenario';

const apiErrorMessage = 'Unable to connect with API service. If this problem persists, please contact an administrator.';
const notFoundErrorMessage = (type) => `Unable to load ${type} information. If this problem persists, please contact an administrator.`;
const exportAttachmentError = 'Unable to generate Scenario Builder Attachment. If this problem persists, please contact an administrator.';
const shareRMFError = (ctrl) => `Unable to share NIST RMF Control ${ctrl}. If this problem persists, please contact an administrator.`;
const appendixError = 'Unable to fetch appendix items. If this problem persists, please contact an administrator.';
const contribSuccessMessage = (type) => `Thank you for your submission! Your ${type} suggestion is now under review.`;

async function fetchScenarioBuilderAppendix({ commit }, type, ids) {
  let appendix = [];
  async function getAppendixItems() {
    return Promise.all(ids.map(async (id) => apiService.get(`${type}/${id}/`)));
  }
  try {
    await getAppendixItems().then((items) => {
      appendix = items;
    });
    return appendix;
  } catch {
    commit('throwError', appendixError);
  }
}

async function fetchPendingCount({ commit }) {
  const moderation = [
    'pendingtools',
    'pendingtags',
    'pendingobjectives',
    'pendingplays'
  ];
  let count = 0;
  async function pinkyPromise() {
    return Promise.all(moderation.map(async (endpoint) => apiService.get(`${endpoint}/?approved=false`)));
  }
  try {
    await pinkyPromise().then((pending) => {
      count = pending.reduce((acc, curr) => (acc + curr.count), 0);
    });
    commit('setPendingCount', count);
  } catch {
    commit('throwError', apiErrorMessage);
  }
}

async function fetchCAPECAppendix({ commit }, plays) {
  let capecList = [];
  let capecAppendix = [];
  let capecIds = plays.flatMap((ply) => ply.attackpatterns.map((capec) => capec.id));
  capecIds = [...new Set(capecIds)];
  capecAppendix = await fetchScenarioBuilderAppendix({ commit }, 'capec', capecIds);
  capecList = plays.flatMap((ply) => ply.attackpatterns.map((capec) => `CAPEC-${capec.id}`));
  capecList = [...new Set(capecList)];
  capecList = capecList.sort();
  return [capecList, capecAppendix];
}

async function fetchATTACKAppendix({ commit }, plays) {
  let teqList = [];
  let teqAppendix = [];
  let teqIds = plays.flatMap((ply) => ply.techniques.map((tq) => tq.id));
  teqIds = [...new Set(teqIds)];
  teqAppendix = await fetchScenarioBuilderAppendix({ commit }, 'techniques', teqIds);
  teqList = plays.flatMap((ply) => ply.techniques.map((tq) => tq.mitre_id));
  teqList = [...new Set(teqList)];
  teqList = teqList.sort();
  return [teqList, teqAppendix];
}

async function fetchNISTAppendix({ commit }, plays) {
  let ctrlList = [];
  let ctrlAppendix = [];
  let ctrlIds = plays.flatMap((ply) => ply.controls.map((ctrl) => ctrl.id));
  ctrlIds = [...new Set(ctrlIds)];
  ctrlAppendix = await fetchScenarioBuilderAppendix({ commit }, 'controls', ctrlIds);
  ctrlList = plays.flatMap((ply) => ply.controls
    .map((ctrl) => [ctrl.sort_id, ctrl.label]));
  ctrlList = ctrlList.sort((a, b) => {
    if (a[0] < b[0]) return -1;
    if (a[0] > b[0]) return 1;
    return 0;
  });
  ctrlList = ctrlList.map(([sortId, label]) => label);
  ctrlList = [...new Set(ctrlList)];
  return [ctrlList, ctrlAppendix];
}

async function fetchCWEAppendix({ commit }, plays) {
  let cweList = [];
  let cweAppendix = [];
  let cweIds = plays.flatMap((ply) => ply.cwes.map((cwe) => cwe.id));
  cweIds = [...new Set(cweIds)];
  cweAppendix = await fetchScenarioBuilderAppendix({ commit }, 'cwe', cweIds);
  cweList = plays.flatMap((ply) => ply.cwes.map((cwe) => `CWE-${cwe.id}`));
  cweList = [...new Set(cweList)];
  cweList = cweList.sort();
  return [cweList, cweAppendix];
}

export default {
  async fetchPlays({ commit }, query) {
    if (query === undefined) query = '';
    try {
      const data = await apiService.get(`plays/${query}`);
      const {
        count,
        next,
        previous,
        results
      } = data;
      commit('setPlays', results);
      commit('setPagination', {
        type: 'plays',
        count,
        next,
        previous
      });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchPlayByID({ commit }, id) {
    commit('loadingApiCall', true);
    try {
      const data = await apiService.get(`plays/${id}/`);
      commit('setSelected', { type: 'play', data });
    } catch {
      commit('throwError', notFoundErrorMessage('play'));
    }
    commit('loadingApiCall', false);
  },
  async fetchPlayOptions({ commit }) {
    try {
      const data = await apiService.get('plays?no_page&ordering=id');
      const options = data.map((option) => {
        const datum = {};
        datum.id = option.id;
        datum.name = option.name;
        datum.display_name = `${option.id} - ${option.name}`;
        return datum;
      });
      commit('setFilterAndFormOptions', { type: 'plays', data: options });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async createPlay({ commit }, data) {
    const pendingPlay = {
      action: 'INS',
      pending_state: data
    };
    try {
      await apiService.post('pendingplays/', pendingPlay);
      commit('showCrudMessage', {
        color: 'info',
        text: contribSuccessMessage('play'),
        type: 'play'
      });
      fetchPendingCount({ commit });
    } catch (err) {
      let msg = 'Unable to add play';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'play' });
      throw err;
    }
  },
  async updatePlay({ commit }, data) {
    const pendingPlay = {
      action: 'UPD',
      target: data.id,
      pending_state: data
    };
    try {
      await apiService.post('pendingplays/', pendingPlay);
      commit('showCrudMessage', {
        color: 'info',
        text: contribSuccessMessage('play'),
        type: 'play'
      });
      fetchPendingCount({ commit });
    } catch (err) {
      let msg = 'Unable to update play';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'play' });
      throw err;
    }
  },
  async moderatePlay({ commit }, pendingPlay) {
    try {
      await apiService.put(`pendingplays/${pendingPlay.id}/`, pendingPlay);
      const msg = pendingPlay.approved ? 'Play approved' : 'Pending play state changed';
      commit('showCrudMessage', { color: 'info', text: msg, type: 'play' });
      fetchPendingCount({ commit });
    } catch (err) {
      const approvalType = pendingPlay.approved ? 'approve' : 'edit';
      const msg = `Unable to ${approvalType} play`;
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'play' });
      throw err;
    }
  },
  async archivePlay({ commit }, play) {
    try {
      await apiService.patch(`plays/${play.id}/`, play);
      commit('showCrudMessage', { color: 'info', text: `<b>${play.name}</b> has been archived`, type: 'play' });
    } catch (err) {
      commit('showCrudMessage', { color: 'danger', text: `<b>${play.name}</b> has NOT been archived`, type: 'play' });
      throw err;
    }
  },
  async fetchCAPECs({ commit }, query) {
    if (query === undefined) query = '';
    try {
      const data = await apiService.get(`capec/${query}`);
      const {
        count,
        next,
        previous,
        results
      } = data;
      commit('setCAPECs', results);
      commit('setPagination', {
        type: 'capecs',
        count,
        next,
        previous
      });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchCAPECByID({ commit }, id) {
    commit('loadingApiCall', true);
    try {
      const data = await apiService.get(`capec/${id}/`);
      commit('setSelected', {
        type: 'capec',
        data: {
          ...data,
          related_plays: data.related_plays
            .sort((a, b) => a.id.slice(2, a.id.length) - b.id.slice(2, b.id.length))
        }
      });
    } catch {
      commit('throwError', notFoundErrorMessage('attack pattern'));
    }
    commit('loadingApiCall', false);
  },
  async fetchTechniques({ commit }, query) {
    if (query === undefined) query = '';
    try {
      const data = await apiService.get(`techniques/${query}`);
      const {
        count,
        next,
        previous,
        results
      } = data;
      commit('setTechniques', results);
      commit('setPagination', {
        type: 'techniques',
        count,
        next,
        previous
      });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchTechniqueByID({ commit }, id) {
    commit('loadingApiCall', true);
    try {
      const data = await apiService.get(`techniques/${id}/`);
      commit('setSelected', {
        type: 'technique',
        data: {
          ...data,
          related_plays: data.related_plays
            .sort((a, b) => a.id.slice(2, a.id.length) - b.id.slice(2, b.id.length))
        }
      });
    } catch {
      commit('throwError', notFoundErrorMessage('technique'));
    }
    commit('loadingApiCall', false);
  },
  async fetchControls({ commit }, query) {
    if (query === undefined) query = '';
    try {
      const data = await apiService.get(`controls/${query}`);
      const {
        count,
        next,
        previous,
        results
      } = data;
      commit('setControls', results);
      commit('setPagination', {
        type: 'controls',
        count,
        next,
        previous
      });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchControlByID({ commit }, id) {
    commit('loadingApiCall', true);
    try {
      const data = await apiService.get(`controls/${id}/`);
      commit('setSelected', {
        type: 'control',
        data: {
          ...data,
          related_plays: data.related_plays
            .sort((a, b) => a.id.slice(2, a.id.length) - b.id.slice(2, b.id.length))
        }
      });
    } catch {
      commit('throwError', notFoundErrorMessage('control'));
    }
    commit('loadingApiCall', false);
  },
  async fetchCWEs({ commit }, query) {
    if (query === undefined) query = '';
    try {
      const data = await apiService.get(`cwe/${query}`);
      const {
        count,
        next,
        previous,
        results
      } = data;
      commit('setCWEs', results);
      commit('setPagination', {
        type: 'cwes',
        count,
        next,
        previous
      });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchCWEByID({ commit }, id) {
    commit('loadingApiCall', true);
    try {
      const data = await apiService.get(`cwe/${id}/`);
      commit('setSelected', {
        type: 'cwe',
        data: {
          ...data,
          related_plays: data.related_plays
            .sort((a, b) => a.id.slice(2, a.id.length) - b.id.slice(2, b.id.length))
        }
      });
    } catch {
      commit('throwError', notFoundErrorMessage('weakness'));
    }
    commit('loadingApiCall', false);
  },
  async fetchOriginalByID({ commit }, [type, id]) {
    try {
      const data = await apiService.get(`${type}s/${id}/`);
      return data;
    } catch {
      commit('throwError', notFoundErrorMessage(type));
      return undefined;
    }
  },
  async fetchToolOptions({ commit }) {
    try {
      const data = await apiService.get('tools?no_page&ordering=name');
      commit('setFilterAndFormOptions', { type: 'tools', data });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async createTool({ commit }, data) {
    const pendingTool = {
      action: 'INS',
      pending_state: data
    };
    try {
      await apiService.post('pendingtools/', pendingTool);
      commit('showCrudMessage', {
        color: 'info',
        text: contribSuccessMessage('tool'),
        type: 'tool'
      });
      fetchPendingCount({ commit });
    } catch (err) {
      let msg = 'Unable to add tool';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'tool' });
      throw Error(err);
    }
  },
  async moderateTool({ commit }, pendingTool) {
    try {
      await apiService.put(`pendingtools/${pendingTool.id}/`, pendingTool);
      const msg = pendingTool.approved ? 'Tool approved' : 'Pending tool state changed';
      commit('showCrudMessage', { color: 'info', text: msg, type: 'tool' });
      fetchPendingCount({ commit });
    } catch (err) {
      const approvalType = pendingTool.approved ? 'approve' : 'edit';
      const msg = `Unable to ${approvalType} tool`;
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'tool' });
      throw err;
    }
  },
  async archiveTool({ commit }, tool) {
    try {
      await apiService.patch(`tools/${tool.id}/`, tool);
      commit('showCrudMessage', { color: 'info', text: `<b>${tool.name}</b> has been archived`, type: 'tool' });
    } catch (err) {
      commit('showCrudMessage', { color: 'danger', text: `<b>${tool.name}</b> has NOT been archived`, type: 'tool' });
      throw err;
    }
  },
  async fetchTagOptions({ commit }) { // BUG: backend sorts caps first, task #231
    try {
      const data = await apiService.get('tags?no_page&ordering=name');
      commit('setFilterAndFormOptions', { type: 'tags', data });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async createTag({ commit }, data) {
    const pendingTag = {
      action: 'INS',
      pending_state: data
    };
    try {
      await apiService.post('pendingtags/', pendingTag);
      commit('showCrudMessage', { color: 'info', text: contribSuccessMessage('tag'), type: 'tag' });
      fetchPendingCount({ commit });
    } catch (err) {
      let msg = 'Unable to add tag';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'tag' });
      throw err;
    }
  },
  async moderateTag({ commit }, pendingTag) {
    try {
      await apiService.put(`pendingtags/${pendingTag.id}/`, pendingTag);
      const msg = pendingTag.approved ? 'Tag approved' : 'Pending tag state changed';
      commit('showCrudMessage', { color: 'info', text: msg, type: 'tag' });
      fetchPendingCount({ commit });
    } catch (err) {
      const approvalType = pendingTag.approved ? 'approve' : 'edit';
      const msg = `Unable to ${approvalType} tag`;
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'tag' });
      throw err;
    }
  },
  async archiveTag({ commit }, tag) {
    try {
      await apiService.patch(`tags/${tag.id}/`, tag);
      commit('showCrudMessage', { color: 'info', text: `<b>${tag.name}</b> has been archived`, type: 'tag' });
    } catch (err) {
      commit('showCrudMessage', { color: 'danger', text: `<b>${tag.name}</b> has NOT been archived`, type: 'tag' });
      throw err;
    }
  },
  async fetchObjectiveOptions({ commit }) {
    try {
      const data = await apiService.get('objectives?no_page&ordering=title');
      commit('setFilterAndFormOptions', { type: 'objectives', data });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async createObjective({ commit }, data) {
    const pendingObjective = {
      action: 'INS',
      pending_state: data
    };
    try {
      await apiService.post('pendingobjectives/', pendingObjective);
      commit('showCrudMessage', {
        color: 'info',
        text: contribSuccessMessage('objective'),
        type: 'objective'
      });
      fetchPendingCount({ commit });
    } catch (err) {
      let msg = 'Unable to add objective';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'objective' });
      throw err;
    }
    fetchPendingCount({ commit });
  },
  async moderateObjective({ commit }, pendingObjective) {
    try {
      await apiService.put(`pendingobjectives/${pendingObjective.id}/`, pendingObjective);
      const msg = pendingObjective.approved
        ? 'Objective approved'
        : 'Pending objective state changed';
      commit('showCrudMessage', { color: 'info', text: msg, type: 'objective' });
      fetchPendingCount({ commit });
    } catch (err) {
      const approvalType = pendingObjective.approved ? 'approve' : 'edit';
      const msg = `Unable to ${approvalType} objective`;
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'objective' });
      throw err;
    }
  },
  async archiveObjective({ commit }, objective) {
    try {
      await apiService.patch(`objectives/${objective.id}/`, objective);
      commit('showCrudMessage', { color: 'info', text: `<b>${objective.title}</b> has been archived`, type: 'objective' });
    } catch (err) {
      commit('showCrudMessage', { color: 'danger', text: `<b>${objective.title}</b> has NOT been archived`, type: 'objective' });
      throw err;
    }
  },
  async fetchUsers({ commit }) {
    try {
      const data = await apiService.get('users/?no_page');
      const users = data.map((usr) => ({
        ...usr.user,
        role: usr.role,
        profile_id: usr.id,
        user_id: usr.user.id
      }));
      commit('setUsers', users);
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async updateProfile({ commit }, user) {
    try {
      await apiService.put(`users/${user.id}/`, user);
      commit('showCrudMessage', { color: 'info', text: 'User updated!', type: 'user' });
    } catch (err) {
      let msg = 'Unable to add update user role';
      msg += typeof err === 'string' ? ` - ${err}` : '';
      commit('showCrudMessage', { color: 'danger', text: msg, type: 'user' });
      throw err;
    }
  },
  async fetchFilterOptions({ commit }) {
    const filters = [
      ['plays', 'id', 'name'],
      ['tags', 'name', 'id'],
      ['objectives', 'title', 'id'],
      ['tactics', 'mitre_id', 'name', '&revoked=false&deprecated=false'],
      ['platforms', 'name'],
      ['groups', 'name', 'id'],
      ['controls', 'id', undefined, '&type=SP800-53'],
      ['controlfamily', 'id', 'name'],
      ['tools', 'name', 'id']
    ];
    let options = [];
    async function pinkyPromise() {
      return Promise.all(filters.map(async ([type, field, field2, filter = '']) => {
        const data = await apiService.get(`${type}?no_page&ordering=${field}${filter}`);
        const options = data.map((option) => {
          const datum = {};
          datum[field] = option[field];
          if (field2) datum[field2] = option[field2];
          if (type === 'plays') {
            datum.display_name = `${option[field]} - ${option[field2]}`;
          }
          if (type === 'tactics') {
            const domain = option.mitre_domain === 'mitre-attack' ? '(E)' : '(M)'
            datum.nickname = `${option[field2]} ${domain}`;
            datum.display_name = `${option[field]} - ${option[field2]} ${domain}`;
          }
          return datum;
        });
        return options;
      }));
    }
    try {
      await pinkyPromise().then((lists) => {
        options = lists;
      });
      const [
        playOptions,
        tagOptions,
        objctvOptions,
        tacticOptions,
        platformOptions,
        groupOptions,
        spControlOptions,
        familyOptions,
        toolOptions
      ] = options;
      commit('setFilterAndFormOptions', { type: 'plays', data: playOptions });
      commit('setFilterAndFormOptions', { type: 'tags', data: tagOptions });
      commit('setFilterAndFormOptions', { type: 'objectives', data: objctvOptions });
      commit('setFilterAndFormOptions', { type: 'tactics', data: tacticOptions });
      commit('setFilterAndFormOptions', { type: 'platforms', data: platformOptions });
      commit('setFilterAndFormOptions', { type: 'groups', data: groupOptions });
      commit('setFilterAndFormOptions', { type: 'spControls', data: spControlOptions });
      commit('setFilterAndFormOptions', { type: 'families', data: familyOptions });
      commit('setFilterAndFormOptions', { type: 'tools', data: toolOptions });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  async fetchFormOptions({ commit }) {
    const filters = [
      ['capec/?', 'name'],
      ['techniques/?revoked=false&deprecated=false&ordering=mitre_id', 'mitre_id', 'name'],
      ['controls/?ordering=sort_id', 'label', 'title'],
      ['cwe/?', 'name']
    ];
    let options = [];
    async function pinkyPromise() {
      return Promise.all(filters.map(async ([endpoint, field, field2]) => {
        const data = await apiService.get(`${endpoint}&no_page`);
        const options = data.map((option) => {
          const datum = {};
          datum.id = option.id;
          if (endpoint === 'capec/?') {
            datum.custom_id = `CAPEC-${option.id}`;
            datum.display_name = `CAPEC-${option.id} - ${option[field]}`;
          } else if (endpoint === 'cwe/?') {
            datum.custom_id = `CWE-${option.id}`;
            datum.display_name = `CWE-${option.id} - ${option[field]}`;
          } else datum.display_name = `${option[field]} - ${option[field2]}`;
          datum[field] = option[field];
          if (field2) datum[field2] = option[field2];
          return datum;
        });
        return options;
      }));
    }
    try {
      await pinkyPromise().then((lists) => {
        options = lists;
      });
      const [
        capecOptions,
        teqOptions,
        ctrlOptions,
        cweOptions
      ] = options;
      commit('setFilterAndFormOptions', { type: 'capecs', data: capecOptions });
      commit('setFilterAndFormOptions', { type: 'techniques', data: teqOptions });
      commit('setFilterAndFormOptions', { type: 'controls', data: ctrlOptions });
      commit('setFilterAndFormOptions', { type: 'cwes', data: cweOptions });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  fetchPendingCount,
  async fetchPending({ commit }) {
    const moderation = [
      'pendingtools',
      'pendingtags',
      'pendingobjectives',
      'pendingplays'
    ];
    let pending = [];
    async function pinkyPromise() {
      return Promise.all(moderation.map(async (endpoint) => apiService.get(`${endpoint}/?no_page&approved=false`)));
    }
    try {
      await pinkyPromise().then((lists) => {
        pending = lists;
      });
      const [
        pendingtools,
        pendingTags,
        pendingObjectives,
        pendingPlays
      ] = pending;
      commit('setPendingApprovals', { type: 'tools', data: pendingtools });
      commit('setPendingApprovals', { type: 'tags', data: pendingTags });
      commit('setPendingApprovals', { type: 'objectives', data: pendingObjectives });
      commit('setPendingApprovals', { type: 'plays', data: pendingPlays });
      fetchPendingCount({ commit });
    } catch {
      commit('throwError', apiErrorMessage);
    }
  },
  updateScenarioBuilder({ commit }, [type, data]) {
    if (type === 'add') commit('addToScenarioBuilder', data);
    else if (type === 'reorder') commit('reorderScenario', data);
    else if (type === 'remove') commit('removeFromScenario', data);
    else if (type === 'details') commit('changeScenarioDetails', data);
    else if (type === 'reset') commit('resetScenario');
  },
  async generateScenarioReport({ commit, state }) {
    commit('loadingApiCall', true);
    let data;
    const { plays, includeAppendix } = state.scenarioBuilder;
    let capecList = [];
    let capecAppendix = [];
    let teqList = [];
    let teqAppendix = [];
    let ctrlList = [];
    let ctrlAppendix = [];
    let cweList = [];
    let cweAppendix = [];

    try {
      if (includeAppendix.capec) {
        const [list, appendix] = await fetchCAPECAppendix({ commit }, plays);
        capecList = list;
        capecAppendix = appendix;
      }
      if (includeAppendix.mitre) {
        const [list, appendix] = await fetchATTACKAppendix({ commit }, plays);
        teqList = list;
        teqAppendix = appendix;
      }
      if (includeAppendix.nist) {
        const [list, appendix] = await fetchNISTAppendix({ commit }, plays);
        ctrlList = list;
        ctrlAppendix = appendix;
      }
      if (includeAppendix.cwe) {
        const [list, appendix] = await fetchCWEAppendix({ commit }, plays);
        cweList = list;
        cweAppendix = appendix;
      }
      data = await exportScenarioToPDF(
        state.scenarioBuilder,
        [includeAppendix.capec, capecList, capecAppendix],
        [includeAppendix.mitre, teqList, teqAppendix],
        [includeAppendix.nist, ctrlList, ctrlAppendix],
        [includeAppendix.cwe, cweList, cweAppendix]
      );
      commit('loadingApiCall', false);
      return data;
    } catch (err) {
      commit('showScenarioBuilder', false);
      commit('throwError', exportAttachmentError);
      commit('loadingApiCall', false);
      throw err;
    }
  },
  async generateScenarioToCSV({ commit, state }) {
    commit('loadingApiCall', true);
    let data;
    const { plays, includeAppendix } = state.scenarioBuilder;
    let capecAppendix = [];
    let teqAppendix = [];
    let ctrlAppendix = [];
    let cweAppendix = [];

    try {
      if (includeAppendix.capec) {
        const [_, appendix] = await fetchCAPECAppendix({ commit }, plays);
        capecAppendix = appendix;
      }
      if (includeAppendix.mitre) {
        const [_, appendix] = await fetchATTACKAppendix({ commit }, plays);
        teqAppendix = appendix;
      }
      if (includeAppendix.nist) {
        const [_, appendix] = await fetchNISTAppendix({ commit }, plays);
        ctrlAppendix = appendix;
      }
      if (includeAppendix.cwe) {
        const [_, appendix] = await fetchCWEAppendix({ commit }, plays);
        cweAppendix = appendix;
      }
      data = await exportScenarioToCSV(
        state.scenarioBuilder,
        [includeAppendix.capec, capecAppendix],
        [includeAppendix.mitre, teqAppendix],
        [includeAppendix.nist, ctrlAppendix],
        [includeAppendix.cwe, cweAppendix]
      );
      commit('loadingApiCall', false);
      return data;
    } catch (err) {
      commit('showScenarioBuilder', false);
      commit('throwError', exportAttachmentError);
      commit('loadingApiCall', false);
      throw err;
    }
  },
  async exportRMFToPDF({ commit }, control) {
    try {
      const data = exportRMFToPDF(control);
      return data;
    } catch (err) {
      commit('showScenarioBuilder', false);
      commit('throwError', shareRMFError(control.label));
      throw err;
    }
  }
};
